import cn from 'classnames';
import { Fragment, type ReactNode, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Divider, Menu, Text } from 'ui';
import { useNavigationMenuConfig } from './hooks';
import { generateNavigationMenuIcon } from './navigation-menu-icon';
import { NavigationMenuLabel } from './navigation-menu-label';

type MenuSection = Array<{
  alias?: string;
  icon: ReactNode;
  id: string;
  key: string;
  label: ReactNode;
  path: string;
}>;

const isActive = (pathname: string, key: string, alias?: string) =>
  Boolean(pathname.startsWith(key) || (alias && pathname.includes(alias)));

const findActiveMenuSectionKey = (pathname: string, menuItems: MenuSection) =>
  menuItems.find((item) => isActive(pathname, item.key, item.alias))?.key;

const getPathFromKey = (key: string, menuItems: MenuSection) => menuItems.find((item) => item.key === key)?.path;

type P = {
  isCollapsed: boolean;
};

const NavigationMenu = ({ isCollapsed }: P) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const navigationMenuConfig = useNavigationMenuConfig();

  const menuSections = useMemo(() => {
    const newMenuSections: MenuSection[] = [];

    for (const menuSection of navigationMenuConfig) {
      const generatedMenuSection: MenuSection = [];

      const section = menuSection.section ?? [];

      for (const menuItem of section) {
        if (!menuItem.isHidden?.()) {
          generatedMenuSection.push({
            alias: menuItem.alias,
            icon: generateNavigationMenuIcon({
              config: menuItem,
              isActive,
              pathname,
            }),
            id: menuItem.id,
            key: menuItem.key,
            label: <NavigationMenuLabel config={menuItem} />,
            path: menuItem.href,
          });
        }
      }

      if (generatedMenuSection.length > 0) {
        newMenuSections.push(generatedMenuSection);
      }
    }

    return newMenuSections;
  }, [navigationMenuConfig, pathname]);

  const onSelect = async ({ key }: { key: number | string }) => {
    const flatMenuSections = menuSections.flat();
    const selectedPath = getPathFromKey(String(key), flatMenuSections);
    const activeKey = findActiveMenuSectionKey(pathname, flatMenuSections);
    const activeKeyPath = activeKey ? getPathFromKey(activeKey, flatMenuSections) : undefined;

    if (!selectedPath) {
      return;
    }

    if (selectedPath === pathname) {
      window.scrollTo(0, 0);
    }

    if (activeKeyPath === selectedPath && selectedPath !== pathname) {
      await navigate(selectedPath);
    }
  };

  return (
    <>
      {menuSections.map((menuSection, index) => {
        const active = findActiveMenuSectionKey(pathname, menuSection);

        return (
          // eslint-disable-next-line react/no-array-index-key -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
          <Fragment key={index}>
            {navigationMenuConfig[index]?.name && navigationMenuConfig[index].collapsedName ? (
              <div className="navigation-menu-text">
                <Text type="secondary">
                  {isCollapsed ? navigationMenuConfig[index].collapsedName : navigationMenuConfig[index].name}
                </Text>
              </div>
            ) : null}
            <Menu
              className="navigation-menu"
              inlineCollapsed={isCollapsed}
              items={menuSection}
              mode="inline"
              multiple
              onDeselect={onSelect}
              onSelect={onSelect}
              selectedKeys={active ? [active] : []}
              theme="light"
            />

            {index < menuSections.length - 1 ? (
              <Divider
                className={cn('navigation-menu-divider', { 'navigation-menu-divider--collapsed': isCollapsed })}
              />
            ) : null}
          </Fragment>
        );
      })}
    </>
  );
};

export default NavigationMenu;
